import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useB3Lang } from '@b3/lang';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';

import { rolePermissionSelector, useAppSelector } from '@/store';
import { InvoiceList } from '@/pages/MultiInvoice/utils/types';
import { snackbar } from '@/utils';

const StyledMenu = styled(Menu)(() => ({
  '& .MuiPaper-elevation': {
    boxShadow:
      '0px 1px 0px -1px rgba(0, 0, 0, 0.1), 0px 1px 6px rgba(0, 0, 0, 0.07), 0px 1px 4px rgba(0, 0, 0, 0.06)',
    borderRadius: '4px',
  },
}));

interface B3PulldownProps {
  row: InvoiceList;
  setIsRequestLoading: (bool: boolean) => void;
}

function B3Pulldown({
  row,
  setIsRequestLoading
}: B3PulldownProps) {
  const ref = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const navigate = useNavigate();

  const b3Lang = useB3Lang();

  const { getOrderPermission } = useAppSelector(rolePermissionSelector);
  const close = () => {
    setIsOpen(false);
  };

  const handleMoreActionsClick = () => {
    setIsOpen(true);
  };

  const handleViewInvoice = async () => {
    const { PDF } = row;

    close();

    setIsRequestLoading(true);

    setIsRequestLoading(false);

    if (!PDF) {
      snackbar.error('pdf url resolution error');
      return;
    }

    const { href } = window.location;
    if (!href.includes('invoice')) {
      return;
    }

    window.open(PDF, '_blank', 'fullscreen=yes');
  };

  const handleViewOrder = () => {
    const { WebOrderId } = row;
    close();
    navigate(`/orderDetail/${WebOrderId}`);
  };

  const handleDownloadPDF = async () => {
    const { PDF } = row;

    close();
    setIsRequestLoading(true);

    setIsRequestLoading(false);

    const a = document.createElement('a');
    a.href = PDF;
    a.target = '_blank';
    a.download = `invoice-${row.InvoiceNo}.pdf`;
    a.click();
  };

  const isPdfAvailable = () => {
    if (row.PDF) {
      return true;
    }
    return false;
  }

  const isOrderIdAvailable = () => {
    return !!row.WebOrderId;
  };

  return (
    <>
      <IconButton onClick={handleMoreActionsClick} ref={ref}>
        <MoreHorizIcon />
      </IconButton>
      <StyledMenu
        id="basic-menu"
        anchorEl={ref.current}
        open={isOpen}
        onClose={close}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >

        {isPdfAvailable() && (
          <MenuItem
            key="View-invoice"
            sx={{
              color: 'primary.main',
            }}
            onClick={handleViewInvoice}
          >
            {b3Lang('invoice.actions.viewInvoice')}
          </MenuItem>
        )}

        {(getOrderPermission && isOrderIdAvailable()) && (
          <MenuItem
            key="View-Order"
            sx={{
              color: 'primary.main',
            }}
            onClick={handleViewOrder}
          >
            {b3Lang('invoice.actions.viewOrder')}
          </MenuItem>
        )}

        {isPdfAvailable() && (
          <MenuItem
            key="Download"
            sx={{
              color: 'primary.main',
            }}
            onClick={handleDownloadPDF}
          >
            {b3Lang('invoice.actions.download')}
          </MenuItem>
        )}

      </StyledMenu>
    </>
  );
}

export default B3Pulldown;
